import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Box,
  Chip,
  IconButton,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { WORKSPACE_RAIL_COUNTS_KEY } from "../hooks/useWorkspaceRailCounts";
import { fetchScheduleReminders, updateUserAgendaEvent } from "../api/client";
import { useNotificationsOptional } from "../context/NotificationContext";
import { NOTIFICATION_TYPES } from "../notifications/types";
import { buildLiveOverdueDigest } from "../notifications/taskOverdueDigest";
import { AGENDA_SEGMENT, TASKS_SEGMENT } from "../utils/teamNavigation";
import {
  readWorkspaceSessionJson,
  writeWorkspaceSessionJson,
  WS_KEY,
} from "../utils/workspaceStorage";

const POLL_MS = 60_000;
const UPCOMING_NOTIFY_TTL_MS = 45 * 60_000;
/** Mesmo conjunto de tarefas atrasadas não reabre o sino a cada minuto. */
const OVERDUE_DIGEST_TTL_MS = 6 * 60 * 60_000;

function loadDismissed() {
  try {
    const arr = readWorkspaceSessionJson(WS_KEY.SCHEDULE_REMINDER_DISMISSED, []);
    return new Set(Array.isArray(arr) ? arr.map(String).filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  try {
    writeWorkspaceSessionJson(WS_KEY.SCHEDULE_REMINDER_DISMISSED, [...set]);
  } catch {
    /* ignore */
  }
}

function loadNotifiedMap() {
  try {
    const raw = readWorkspaceSessionJson(WS_KEY.SCHEDULE_REMINDER_NOTIFIED, {});
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function saveNotifiedMap(map) {
  try {
    writeWorkspaceSessionJson(WS_KEY.SCHEDULE_REMINDER_NOTIFIED, map);
  } catch {
    /* ignore */
  }
}

function parseLooseDate(input) {
  if (!input) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  if (!s) return null;
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(dt, locale = "pt-BR") {
  if (!dt) return null;
  try {
    return dt.toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function formatRelativePast(ms, locale = "pt-BR") {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < 45_000) return rtf.format(-1, "second");
  if (abs < hour) return rtf.format(-Math.max(1, Math.round(abs / minute)), "minute");
  if (abs < day) return rtf.format(-Math.max(1, Math.round(abs / hour)), "hour");
  return rtf.format(-Math.max(1, Math.round(abs / day)), "day");
}

function formatRelativeFuture(ms, locale = "pt-BR") {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(ms);
  const minute = 60_000;
  const hour = 60 * minute;
  if (abs < 45_000) return rtf.format(0, "minute");
  if (abs < hour) return rtf.format(Math.max(1, Math.round(abs / minute)), "minute");
  return rtf.format(Math.max(1, Math.round(abs / hour)), "hour");
}

function isAgendaPath(pathname) {
  return String(pathname || "").includes(AGENDA_SEGMENT);
}

function isTasksPath(pathname) {
  return String(pathname || "").includes(TASKS_SEGMENT);
}

function notificationTypeForItem(item) {
  if (item.kind === "task") {
    return item.phase === "overdue" ? NOTIFICATION_TYPES.task_overdue : NOTIFICATION_TYPES.task_due;
  }
  return NOTIFICATION_TYPES.agenda_reminder;
}

function isModalPhase(phase) {
  return phase === "overdue" || phase === "due";
}

/**
 * Lembretes inteligentes de agenda (plataforma + Google) e tarefas.
 * Modal para itens vencidos/ativos; toast/som/desktop via NotificationContext para próximos.
 */
export default function ScheduleDueReminder() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [markingId, setMarkingId] = useState(null);
  const dismissed = useRef(loadDismissed());
  const notifiedAtRef = useRef(loadNotifiedMap());
  const showingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const notifications = useNotificationsOptional();
  const { t, i18n } = useTranslation("notifications");

  const locale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";

  const pruneNotified = useCallback(() => {
    const now = Date.now();
    const map = notifiedAtRef.current;
    let changed = false;
    for (const key of Object.keys(map)) {
      const maxAge = String(key).startsWith("overdue-digest:")
        ? OVERDUE_DIGEST_TTL_MS
        : UPCOMING_NOTIFY_TTL_MS;
      if (now - (map[key] || 0) > maxAge) {
        delete map[key];
        changed = true;
      }
    }
    if (changed) saveNotifiedMap(map);
  }, []);

  const dispatchScheduleAlerts = useCallback(
    (rows, { overdueRows: overdueSource, overdueTotal: totalOverdue = 0 } = {}) => {
      if (!notifications?.dispatch) return;
      const tabVisible = typeof document !== "undefined" && document.visibilityState === "visible";
      const onAgenda = isAgendaPath(location.pathname);
      const onTasks = isTasksPath(location.pathname);
      const overdueRows = Array.isArray(overdueSource)
        ? overdueSource
        : rows.filter((row) => row?.kind === "task" && row?.phase === "overdue");
      const individualRows = rows.filter((row) => !(row?.kind === "task" && row?.phase === "overdue"));

      const digest = buildLiveOverdueDigest({
        tasks: overdueRows,
        total: totalOverdue,
        locale,
        t,
      });
      if (digest && notifications?.prefs?.categories?.[NOTIFICATION_TYPES.task_overdue] !== false) {
        const notifyKey = `overdue-digest:${digest.signature}`;
        const last = notifiedAtRef.current[notifyKey] || 0;
        if (Date.now() - last >= OVERDUE_DIGEST_TTL_MS) {
          const focused = tabVisible && onTasks;
          notifications.dispatch({
            type: NOTIFICATION_TYPES.task_overdue,
            title: digest.title,
            body: digest.body,
            deepLink: digest.deepLink,
            dedupeKey: "schedule-overdue-digest",
            replaceTaskOverdue: true,
            skipToast: tabVisible,
            skipDesktop: focused,
            skipSound: tabVisible,
          });
          notifiedAtRef.current[notifyKey] = Date.now();
        }
      }

      for (const row of individualRows) {
        const isUpcoming = row.phase === "upcoming";
        const isActionable = isModalPhase(row.phase);
        if (!isUpcoming && !isActionable) continue;

        const phaseKey = isUpcoming ? "upcoming" : row.phase;
        const notifyKey = `${row.id}:${phaseKey}`;
        const last = notifiedAtRef.current[notifyKey] || 0;
        const ttl = isUpcoming ? UPCOMING_NOTIFY_TTL_MS : 20 * 60_000;
        if (Date.now() - last < ttl) continue;

        const type = notificationTypeForItem(row);
        const focused =
          tabVisible &&
          ((row.kind === "agenda" && onAgenda) || (row.kind === "task" && onTasks));

        let title;
        if (row.kind === "task") {
          title =
            row.phase === "overdue"
              ? t("titles.taskOverdue")
              : row.phase === "upcoming"
                ? t("titles.taskUpcoming")
                : t("titles.taskDue");
        } else {
          title =
            row.phase === "upcoming"
              ? row.source === "google"
                ? t("titles.agendaUpcomingGoogle")
                : t("titles.agendaUpcoming")
              : row.source === "google"
                ? t("titles.agendaDueGoogle")
                : t("titles.agendaDue");
        }
        const body = row.title || "";

        if (notifications?.prefs?.categories?.[type] === false) continue;

        const skipToastForModal = isActionable && tabVisible;

        notifications.dispatch({
          type,
          title,
          body,
          deepLink: row.deepLink,
          dedupeKey: `schedule-${notifyKey}`,
          skipToast: skipToastForModal,
          skipDesktop: focused || (isActionable && tabVisible),
        });

        notifiedAtRef.current[notifyKey] = Date.now();
      }
      saveNotifiedMap(notifiedAtRef.current);
    },
    [locale, location.pathname, notifications, t]
  );

  const poll = useCallback(async () => {
    pruneNotified();
    try {
      const payload = await fetchScheduleReminders();
      const list = Array.isArray(payload?.items) ? payload.items : [];
      const overdueRows = list.filter(
        (row) => row?.kind === "task" && row?.phase === "overdue" && row?.id != null
      );
      const reportedTotal = Number(payload?.overdueTotal);
      const totalOverdue =
        Number.isFinite(reportedTotal) && reportedTotal >= 0 ? reportedTotal : overdueRows.length;
      setOverdueTotal(totalOverdue);
      const filtered = list.filter((row) => row?.id != null && !dismissed.current.has(String(row.id)));

      dispatchScheduleAlerts(filtered, { overdueRows, overdueTotal: totalOverdue });

      const modalItems = filtered.filter((row) => isModalPhase(row.phase));
      const tabVisible = typeof document !== "undefined" && document.visibilityState === "visible";

      if (modalItems.length && tabVisible && !showingRef.current) {
        showingRef.current = true;
        setItems(modalItems);
        setOpen(true);
        return;
      }

      if (!modalItems.length && showingRef.current) {
        setItems([]);
        setOpen(false);
        showingRef.current = false;
      }
    } catch {
      /* ignore */
    }
  }, [dispatchScheduleAlerts, pruneNotified]);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);

  const dismissMany = useCallback((rows) => {
    rows.forEach((x) => {
      if (x?.id != null) dismissed.current.add(String(x.id));
    });
    saveDismissed(dismissed.current);
    queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
  }, [queryClient]);

  const handleClose = useCallback(() => {
    dismissMany(items);
    showingRef.current = false;
    setOpen(false);
  }, [dismissMany, items]);

  const goToItem = useCallback(
    (row) => {
      dismissMany([row]);
      showingRef.current = false;
      setOpen(false);
      if (row?.deepLink) navigate(row.deepLink);
    },
    [dismissMany, navigate]
  );

  const dismissOne = useCallback(
    (row) => {
      dismissMany([row]);
      setItems((prev) => {
        const next = prev.filter((x) => String(x?.id) !== String(row?.id));
        if (!next.length) {
          showingRef.current = false;
          setOpen(false);
        }
        return next;
      });
    },
    [dismissMany]
  );

  const markAgendaStatus = useCallback(
    async (row, status) => {
      if (row?.kind !== "agenda" || !row?.eventId) return;
      const actionKey = `${row.id}:${status}`;
      setMarkingId(actionKey);
      try {
        await updateUserAgendaEvent(row.eventId, { status });
        dismissOne(row);
        queryClient.invalidateQueries(["user-agenda"]);
        queryClient.invalidateQueries(WORKSPACE_RAIL_COUNTS_KEY);
      } catch {
        /* o próximo poll tenta novamente; não interrompe o lembrete */
      } finally {
        setMarkingId(null);
      }
    },
    [dismissOne, queryClient]
  );

  const sortedItems = useMemo(() => {
    const clone = Array.isArray(items) ? [...items] : [];
    const rank = { overdue: 0, due: 1, upcoming: 2 };
    const toTs = (row) => parseLooseDate(row?.at)?.getTime() ?? Number.POSITIVE_INFINITY;
    clone.sort((a, b) => {
      const pr = (rank[a.phase] ?? 9) - (rank[b.phase] ?? 9);
      if (pr !== 0) return pr;
      return toTs(a) - toTs(b);
    });
    return clone;
  }, [items]);

  const firstItem = sortedItems[0];
  const overdueCount = sortedItems.filter((x) => x.phase === "overdue").length;
  const dueCount = sortedItems.filter((x) => x.phase === "due").length;

  const dialogTitle = useMemo(() => {
    if (overdueCount && dueCount) return t("scheduleReminder.titleMixed");
    if (overdueCount) return t("scheduleReminder.titleOverdue");
    return t("scheduleReminder.titleDue");
  }, [dueCount, overdueCount, t]);

  const dialogSubtitle = useMemo(() => {
    if (overdueCount && dueCount) return t("scheduleReminder.subtitleMixed");
    if (overdueCount && overdueTotal > overdueCount) {
      return t("scheduleReminder.subtitleOverdueMore", {
        shown: overdueCount,
        total: overdueTotal,
      });
    }
    if (overdueCount) return t("scheduleReminder.subtitleOverdue");
    return t("scheduleReminder.subtitleDue");
  }, [dueCount, overdueCount, overdueTotal, t]);

  const dialogPaperSx = {
    borderRadius: 3,
    overflow: "hidden",
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 24px 70px ${alpha("#000", 0.55)}`
        : `0 24px 70px ${alpha("#000", 0.2)}`,
  };

  const headerSx = {
    pb: 1.5,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.background.paper, 0)})`
        : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.background.paper, 0)})`,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
      aria-labelledby="schedule-due-title"
    >
      <DialogTitle id="schedule-due-title" sx={headerSx}>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12),
              color: theme.palette.primary.main,
              flex: "0 0 auto",
              mt: 0.25,
            }}
          >
            <NotificationsActiveOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.2 }} noWrap>
                {dialogTitle}
              </Typography>
              <Chip
                size="small"
                label={`${sortedItems.length}`}
                sx={{
                  fontWeight: 700,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.main, 0.18)
                      : alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.35 }}>
              {dialogSubtitle}
            </Typography>
          </Box>

          <Tooltip title={t("scheduleReminder.dismissTooltip")}>
            <IconButton onClick={handleClose} size="small" aria-label={t("scheduleReminder.closeAria")}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 1.5,
          pb: 0,
          maxHeight: { xs: "72vh", sm: "70vh" },
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.55 : 0.9)}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <List
            dense
            disablePadding
            sx={{
              "& .MuiListItemButton-root": {
                py: 1.1,
                px: 1.25,
                alignItems: "stretch",
                gap: 1,
              },
            }}
          >
            {sortedItems.map((row, idx) => {
              const dt = parseLooseDate(row?.at);
              const ts = dt?.getTime?.();
              const isOverdue = row.phase === "overdue";
              const isDue = row.phase === "due";
              const relPast =
                typeof ts === "number" && (isOverdue || isDue)
                  ? formatRelativePast(Date.now() - ts, locale)
                  : null;
              const relFuture =
                typeof ts === "number" && row.phase === "upcoming"
                  ? formatRelativeFuture(ts - Date.now(), locale)
                  : null;
              const abs = dt ? formatWhen(dt, locale) : null;
              const title = row?.title || (row.kind === "task" ? "Tarefa" : "Compromisso");
              const KindIcon = row.kind === "task" ? AssignmentOutlinedIcon : EventOutlinedIcon;

              return (
                <React.Fragment key={row.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => goToItem(row)}
                      sx={{
                        "&:hover .goIcon": { transform: "translateX(2px)", opacity: 1 },
                        "&:focus-visible": {
                          outline: `3px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha(
                            isOverdue ? theme.palette.error.main : theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.2 : 0.1
                          ),
                          color: isOverdue ? theme.palette.error.main : theme.palette.primary.main,
                          flex: "0 0 auto",
                          alignSelf: "center",
                        }}
                      >
                        <KindIcon sx={{ fontSize: 18 }} />
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap title={title}>
                            {title}
                          </Typography>
                          {row.kind === "task" && row.boardName ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "text.secondary",
                                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.06),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 999,
                                flex: "0 0 auto",
                              }}
                              noWrap
                            >
                              {row.boardName}
                            </Typography>
                          ) : null}
                          {row.source === "google" ? (
                            <Chip
                              size="small"
                              label="Google"
                              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                            />
                          ) : null}
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.35, minWidth: 0 }}>
                          {abs ? (
                            <Typography variant="body2" color="text.secondary" noWrap title={abs}>
                              {abs}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {t("scheduleReminder.noTime")}
                            </Typography>
                          )}

                          {relPast ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: isOverdue ? theme.palette.error.main : theme.palette.warning.main,
                                bgcolor: alpha(
                                  isOverdue ? theme.palette.error.main : theme.palette.warning.main,
                                  theme.palette.mode === "dark" ? 0.16 : 0.12
                                ),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 999,
                                flex: "0 0 auto",
                              }}
                              title={abs || ""}
                            >
                              {relPast}
                            </Typography>
                          ) : null}

                          {relFuture ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.info.main,
                                bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.16 : 0.12),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 999,
                                flex: "0 0 auto",
                              }}
                            >
                              {relFuture}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>

                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: "0 0 auto" }}>
                        {row.kind === "agenda" ? (
                          <>
                            <Tooltip title={t("scheduleReminder.markCompleted")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={markingId === `${row.id}:completed`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    markAgendaStatus(row, "completed");
                                  }}
                                  aria-label={t("scheduleReminder.markCompletedAria", { title })}
                                >
                                  <CheckCircleOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t("scheduleReminder.markCanceled")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={markingId === `${row.id}:canceled`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    markAgendaStatus(row, "canceled");
                                  }}
                                  aria-label={t("scheduleReminder.markCanceledAria", { title })}
                                >
                                  <CancelOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : null}
                        <Tooltip title={t("scheduleReminder.dismissOne")}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              dismissOne(row);
                            }}
                            aria-label={t("scheduleReminder.dismissOneAria", { title })}
                          >
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <ArrowForwardRoundedIcon
                          className="goIcon"
                          fontSize="small"
                          style={{ opacity: 0.65, transition: "transform 120ms ease, opacity 120ms ease" }}
                        />
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                  {idx < sortedItems.length - 1 ? <Divider component="li" /> : null}
                </React.Fragment>
              );
            })}

            {!sortedItems.length ? (
              <Box sx={{ p: 2.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t("scheduleReminder.emptyTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("scheduleReminder.emptyBody")}
                </Typography>
              </Box>
            ) : null}
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" variant="text">
          {t("scheduleReminder.dismissAll")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => firstItem && goToItem(firstItem)}
          variant="contained"
          disableElevation
          disabled={!firstItem}
          startIcon={<ArrowForwardRoundedIcon />}
        >
          {t("scheduleReminder.openFirst")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
